/**
 * Transform bindings — editor API for `PropertiesData.transform`, the base
 * bound transform (CANOPY_STATES §5.1.1).
 *
 * A component of the object's own pose can be a `{binding}` to a number
 * property declared on the object or an ancestor: `[0, "{deckHeight}", 0]`
 * makes the object sit at whatever the deck height resolves to, and follow it
 * when it changes. This module is what an inspector's transform row calls to
 * show that binding as a chip, bind an axis, or bake it back to a literal.
 *
 * Position and scale only. Rotation is authored as a quaternion tuple whose
 * components mean nothing to an author as numbers — binding one would be a
 * trap, not a feature.
 */

import type { SceneObject } from '../core/SceneObject';
import type { ValidationError } from '../scenedata/behaviordata';
import type { PropertiesData, TransformComponent, TransformOverride } from '../scenedata/behaviors/propertiesdata';
import { numericTuple } from '../scenedata/behaviors/propertiesdata';
import { boundPropertyName } from '../behaviors/tracebehavior';
import { Properties } from '../behaviors/properties';
import type { UndoCommand } from './undo/UndoCommand';
import { getUndoManager } from './undo/UndoManager';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from './undo/AddBehaviorCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { findPropertyProvider } from './property-provider';

/** The transform channels an axis can be bound on. */
export type BindableTransformChannel = 'position' | 'scale';
export type TransformAxis = 0 | 1 | 2;

/** A number-typed declaration reachable from an object (self or ancestor). */
export interface BindableNumberProperty {
    /** Declaration name — the `{name}` a component binds to. */
    name: string;
    /** Display label (declaration `label` ?? name). */
    label: string;
    /** Object whose Properties behavior declares it. */
    provider: SceneObject;
}

/** Per-axis binding names for the two bindable channels (`null` = literal). */
export interface TransformBindings {
    position: [string | null, string | null, string | null];
    scale: [string | null, string | null, string | null];
}

export interface TransformBindingResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** Undo command for the write (null on failure / no-op). */
    command: UndoCommand | null;
}

export interface TransformBindingOptions {
    /** Push the command onto the undo stack (default true). */
    record?: boolean;
}

/**
 * The number-typed declarations reachable from `sceneObject` — its own
 * Properties plus ancestors, nearest first, de-duplicated by name (a nearer
 * declaration shadows a same-named ancestor one, matching the provider walk).
 */
export function listBindableNumberProperties(sceneObject: SceneObject): BindableNumberProperty[] {
    const out: BindableNumberProperty[] = [];
    const seen = new Set<string>();
    let owner: SceneObject | null = sceneObject;
    while (owner) {
        const props = owner.getBehavior(Properties);
        for (const d of props?.getData().properties ?? []) {
            // Every name at a level shadows the same name further up, whatever
            // its type — the provider walk that a bind resolves through is
            // type-blind, so listing a shadowed number would offer a binding
            // that then fails with WRONG_TYPE.
            if (seen.has(d.name)) continue;
            seen.add(d.name);
            if (d.type !== 'number') continue;
            out.push({ name: d.name, label: d.label ?? d.name, provider: owner });
        }
        owner = owner.getParent();
    }
    return out;
}

/** The property an axis is bound to, or `null` when it is a literal / unset. */
export function getTransformBinding(
    sceneObject: SceneObject,
    channel: BindableTransformChannel,
    axis: TransformAxis,
): string | null {
    const tuple = sceneObject.getBehavior(Properties)?.getData().transform?.[channel];
    const component = tuple?.[axis];
    return typeof component === 'string' ? boundPropertyName(component) : null;
}

/** Every binding on the object's base transform, per channel and axis. */
export function getTransformBindings(sceneObject: SceneObject): TransformBindings {
    const read = (channel: BindableTransformChannel): [string | null, string | null, string | null] => [
        getTransformBinding(sceneObject, channel, 0),
        getTransformBinding(sceneObject, channel, 1),
        getTransformBinding(sceneObject, channel, 2),
    ];
    return { position: read('position'), scale: read('scale') };
}

/**
 * Bind one axis of the object's base transform to a number property.
 *
 * The other components of that channel keep their current authored values,
 * written as literals so the tuple is complete. A missing Properties behavior
 * is added (one undo bundle). The property must resolve from the object —
 * declared on it or an ancestor — and be number-typed.
 */
export async function bindTransformComponent(
    sceneObject: SceneObject,
    channel: BindableTransformChannel,
    axis: TransformAxis,
    propertyName: string,
    options: TransformBindingOptions = {},
): Promise<TransformBindingResult> {
    const provider = findPropertyProvider(sceneObject, propertyName);
    const declaration = provider?.properties.getData().properties.find(d => d.name === propertyName);
    if (!declaration) {
        return { errors: [{ message: `"${propertyName}" is not declared on "${sceneObject.name}" or an ancestor`, code: 'NO_PROPERTY' }], command: null };
    }
    if (declaration.type !== 'number') {
        return { errors: [{ message: `"${propertyName}" is ${declaration.type}, not number`, code: 'WRONG_TYPE' }], command: null };
    }

    const commands: UndoCommand[] = [];
    let props = sceneObject.getBehavior(Properties);
    if (!props) {
        props = await sceneObject.addBehavior(Properties);
        commands.push(new AddBehaviorCommand(sceneObject, 'Properties', props.getData()));
    }

    const before = props.getData();
    const tuple = channelTuple(sceneObject, props, before.transform, channel);
    if (tuple[axis] === `{${propertyName}}`) return { errors: [], command: null };
    tuple[axis] = `{${propertyName}}`;

    const errors = await props.commit({ ...before, transform: { ...before.transform, [channel]: tuple } });
    if (errors.length > 0) return { errors, command: null };
    commands.push(new BehaviorCommitCommand(sceneObject, 'Properties', before, props.getData(), null));

    return finish(sceneObject, commands, `Bind ${sceneObject.name} ${channel}.${'xyz'[axis]} to ${propertyName}`, options);
}

/**
 * Replace a bound axis with its current resolved value, so the pose is
 * unchanged but no longer follows the property. A channel left with no
 * bindings is dropped from the base transform (an all-literal tuple only
 * restates the authored pose), and so is an emptied `transform`. No-op
 * success when the axis isn't bound.
 */
export async function unbindTransformComponent(
    sceneObject: SceneObject,
    channel: BindableTransformChannel,
    axis: TransformAxis,
    options: TransformBindingOptions = {},
): Promise<TransformBindingResult> {
    const props = sceneObject.getBehavior(Properties);
    const before = props?.getData();
    const authoredTuple = before?.transform?.[channel];
    const component = authoredTuple?.[axis];
    const name = typeof component === 'string' ? boundPropertyName(component) : null;
    if (!props || !before || !authoredTuple || !name) return { errors: [], command: null };

    const resolved = findPropertyProvider(sceneObject, name)?.properties.getValue(name);
    const literal = typeof resolved === 'number' && Number.isFinite(resolved)
        ? resolved
        : liveComponent(sceneObject, channel, axis);

    const tuple = [...authoredTuple] as [TransformComponent, TransformComponent, TransformComponent];
    tuple[axis] = literal;
    const transform: TransformOverride = { ...before.transform };
    if (numericTuple(tuple)) delete transform[channel]; else transform[channel] = tuple;
    const next: PropertiesData = { ...before };
    if (Object.keys(transform).length > 0) next.transform = transform; else delete next.transform;

    // Wholesale, not a patch: `commit` shallow-merges, which cannot remove
    // the `transform` key an all-literal result leaves behind.
    const errors = await props.commitSnapshot(next);
    if (errors.length > 0) return { errors, command: null };

    // The pose is now plain authored data: write the literal into the node
    // and capture, so the authored transform records what the binding left.
    writeLiveComponent(sceneObject, channel, axis, literal);
    sceneObject.captureTransform();

    const command = new BehaviorCommitCommand(sceneObject, 'Properties', before, props.getData(), null);
    return finish(sceneObject, [command], `Unbind ${sceneObject.name} ${channel}.${'xyz'[axis]}`, options);
}

// ── internals ──────────────────────────────────────────────────────────────

/**
 * The channel's tuple to edit: the authored base tuple when there is one,
 * else the authored pose as literals (what the binding will be overlaid on).
 */
function channelTuple(
    sceneObject: SceneObject,
    props: Properties,
    transform: TransformOverride | undefined,
    channel: BindableTransformChannel,
): [TransformComponent, TransformComponent, TransformComponent] {
    const existing = transform?.[channel];
    if (existing) return [...existing] as [TransformComponent, TransformComponent, TransformComponent];
    const authored = props.getAuthoredTransform();
    const vector = authored
        ? (channel === 'position' ? authored.position : authored.scale)
        : (channel === 'position' ? sceneObject.transform.position : sceneObject.transform.scaling);
    return [vector.x, vector.y, vector.z];
}

function liveComponent(sceneObject: SceneObject, channel: BindableTransformChannel, axis: TransformAxis): number {
    const vector = channel === 'position' ? sceneObject.transform.position : sceneObject.transform.scaling;
    return vector.asArray()[axis];
}

function writeLiveComponent(sceneObject: SceneObject, channel: BindableTransformChannel, axis: TransformAxis, value: number): void {
    const vector = channel === 'position' ? sceneObject.transform.position : sceneObject.transform.scaling;
    if (axis === 0) vector.x = value; else if (axis === 1) vector.y = value; else vector.z = value;
}

function finish(
    sceneObject: SceneObject,
    commands: UndoCommand[],
    label: string,
    options: TransformBindingOptions,
): TransformBindingResult {
    const command = commands.length === 1 ? commands[0] : new CompositeCommand(label, commands);
    if (options.record !== false && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    dispatchBehaviorChanged({ objectPath: sceneObject.getPath(), behaviorType: 'Properties', action: 'updated' });
    return { errors: [], command };
}
